package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import org.example.project.dashboard.ScreenManager
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.DashboardStyles
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.SidebarMode
import org.example.project.enginesmartring.EngineSmartRingSettingsStyles
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private fun svgName(path: String): String =
    path.substringAfterLast('/').ifBlank { path }

@Composable
private fun DashboardIndexScreenRow(screen: All) {
    Div({
        classes(DashboardStyles.screenRow, DashboardStyles.navItemStatic)
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Img(screen.icon.ifBlank { "images/step.svg" }) {
            classes(DashboardStyles.stepIcon)
            style {
                width(40.px)
                height(40.px)
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
                minWidth(0.px)
            }
            }) {
                Span({
                    style {
                        maxWidth(100.percent)
                    }
                }) { Text(screen.label.ifBlank { screen.key }) }
                Span({
                    style {
                        maxWidth(100.percent)
                    }
                }) { Text(svgName(screen.icon)) }
        }
    }
}

@Composable
private fun DashboardIndexCategoryBlock(
    title: String,
    icon: String,
    screens: List<All>
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            paddingTop(14.px)
            paddingBottom(14.px)
            property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
            property("border-radius", "18px")
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("box-sizing", "border-box")
            overflow("hidden")
        }
    }) {
        Div({
            classes(DashboardStyles.categoryRow, DashboardStyles.navItemStatic)
            style {
                paddingLeft(24.px)
                paddingRight(24.px)
                marginTop(0.px)
            }
        }) {
            if (icon.isNotBlank()) {
                Img(icon) {
                    classes(DashboardStyles.categoryIcon)
                    style {
                        width(50.px)
                        height(50.px)
                    }
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(2.px)
                    minWidth(0.px)
                }
            }) {
                Span({ classes(DashboardStyles.categoryText) }) { Text(title) }
                Span { Text(svgName(icon)) }
            }
        }

        screens.forEach { screen ->
            DashboardIndexScreenRow(screen)
        }
    }
}

@Composable
private fun DashboardIndexNodeBlock(
    nodeTitle: String,
    nodeKey: String
) {
    val dashboard = ScreenManager.rawDashboardIndex(nodeKey)
    val categoryIcons = dashboard.categoryIcons

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(18.px)
            marginBottom(30.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                paddingLeft(36.px)
                paddingRight(24.px)
            }
        }) {
            H2({
                classes(EngineSmartRingSettingsStyles.title)
                style {
                    marginTop(0.px)
                    marginBottom(0.px)
                }
            }) { Text(nodeTitle) }
        }

        if (dashboard.typ == SidebarMode.CATEGORIZED) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(280px, 1fr))")
                    gap(18.px)
                    width(100.percent)
                }
            }) {
                dashboard.module.forEach { category ->
                    DashboardIndexCategoryBlock(
                        title = category.bezeichnung,
                        icon = categoryIcons[category.bezeichnung].orEmpty(),
                        screens = category.columnSettings
                    )
                }
            }

            if (dashboard.lizenzen.isNotEmpty()) {
                Div({ classes(DashboardStyles.divider) })
                DashboardIndexCategoryBlock(
                    title = "Fenster / Topbar",
                    icon = "images/apps.svg",
                    screens = dashboard.lizenzen
                )
            }
        }
    }
}

@Composable
fun SysDashboardAuskunftScreen() {
    Style(EngineSmartRingSettingsStyles)
    Div({
        classes(EngineSmartRingSettingsStyles.screen)
        style {
            position(Position.Relative)
            paddingLeft(0.px)
            paddingRight(0.px)
            paddingTop(12.px)
            paddingBottom(30.px)
        }
    }) {
        Div({
            classes(EngineSmartRingSettingsStyles.dialog)
            style {
                maxWidth(100.percent)
                width(100.percent)
                paddingLeft(24.px)
                paddingRight(24.px)
            }
        }) {
            Div({
                classes(EngineSmartRingSettingsStyles.body)
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(18.px)
                }
            }) {
                DashboardIndexNodeBlock("SYS Dashboard", DashboardNode.SYS)
                Div({ classes(DashboardStyles.divider) })
                DashboardIndexNodeBlock("CUS Dashboard", DashboardNode.CUS)
                Div({ classes(DashboardStyles.divider) })
                DashboardIndexNodeBlock("NET Dashboard", DashboardNode.NET)
            }
        }
    }
}
